import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  defaultsFor,
  experimentIds,
  getExperiment,
  SimulationRunner,
} from '@physlab/core';
import { TeachingAgent } from '@physlab/teaching-agent';
import { TopBar } from './components/TopBar.jsx';
import { SceneCanvas } from './components/SceneCanvas.jsx';
import { TransportBar } from './components/TransportBar.jsx';
import { ParameterPanel } from './components/ParameterPanel.jsx';
import { DataPanel } from './components/DataPanel.jsx';
import { AgentPanel } from './components/AgentPanel.jsx';
import { TelemetryChart } from './components/TelemetryChart.jsx';
import { ConservationCard } from './components/ConservationCard.jsx';
import { DiagnosticsDrawer } from './components/DiagnosticsDrawer.jsx';
import { Toast } from './components/Toast.jsx';
import { ErrorBoundary } from './components/ErrorBoundary.jsx';
import { Icon } from './components/Icons.jsx';
import { getRenderer } from './renderers/index.js';
import { downloadTextFile } from './lib/download.js';
import { paramsFingerprint } from './lib/format.js';
import {
  clearRuntimeIncidents,
  getRuntimeIncidents,
  subscribeRuntimeIncidents,
} from './lib/runtimeLog.js';

const PANELS = ['params', 'data', 'agent'];
const SAMPLE_INTERVAL = 0.075;
const MAX_SAMPLES = 480;

const MODEL_LEGENDS = {
  pendulum: [
    { className: 'velocity', label: '速度' },
    { className: 'gravity', label: '重力' },
    { className: 'normal', label: '绳张力' },
  ],
  collision: [
    { className: 'velocity', label: '速度' },
    { className: 'center', label: '质心' },
  ],
  incline: [
    { className: 'gravity', label: '重力' },
    { className: 'normal', label: '支持力' },
    { className: 'friction', label: '摩擦力' },
  ],
  circular: [
    { className: 'velocity', label: '速度' },
    { className: 'gravity', label: '重力' },
    { className: 'centripetal', label: '向心力' },
  ],
};

function readQuery() {
  if (typeof window === 'undefined') return { lab: 'pendulum', panel: 'params' };
  const params = new URLSearchParams(window.location.search);
  const lab = params.get('lab');
  const panel = params.get('panel');
  return {
    lab: experimentIds.includes(lab) ? lab : 'pendulum',
    panel: PANELS.includes(panel) ? panel : 'params',
  };
}

const initialQuery = readQuery();

export default function App() {
  const [labId, setLabId] = useState(initialQuery.lab);
  const [panel, setPanel] = useState(initialQuery.panel);
  const [presentation, setPresentation] = useState(false);
  const model = getExperiment(labId);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('lab', labId);
    url.searchParams.set('panel', panel);
    window.history.replaceState({}, '', url);
  }, [labId, panel]);

  useEffect(() => {
    document.title = `${model.meta.title} · PhysLab 仿真教学台`;
  }, [model]);

  const selectLab = useCallback((id) => {
    if (!experimentIds.includes(id)) return;
    setLabId(id);
  }, []);

  return (
    <div
      className={`app-shell ${presentation ? 'is-presentation' : ''}`}
      style={{ '--accent': model.meta.accent, '--accent-rgb': model.meta.accentRgb }}
    >
      <ErrorBoundary>
        <LabWorkspace
          key={labId}
          labId={labId}
          model={model}
          panel={panel}
          onPanel={setPanel}
          onSelectLab={selectLab}
          presentation={presentation}
          onTogglePresentation={() => setPresentation((value) => !value)}
        />
      </ErrorBoundary>
    </div>
  );
}

function LabWorkspace({
  labId,
  model,
  panel,
  onPanel,
  onSelectLab,
  presentation,
  onTogglePresentation,
}) {
  const agent = useMemo(() => new TeachingAgent(), []);
  const runner = useMemo(() => new SimulationRunner(model), [model]);
  const renderer = useMemo(() => getRenderer(model.id), [model.id]);
  const stageRef = useRef(null);
  const sampleRef = useRef([]);
  const generationTimer = useRef(null);

  const [params, setParams] = useState(() => ({ ...runner.params }));
  const [snapshot, setSnapshot] = useState(() => runner.getSnapshot());
  const [running, setRunning] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [samples, setSamples] = useState([]);
  const [options, setOptions] = useState({ vectors: true, grid: true, trail: true });
  const [resetToken, setResetToken] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [difficulty, setDifficulty] = useState('standard');
  const [counts, setCounts] = useState({ exercises: 4, thinking: 2 });
  const [generating, setGenerating] = useState(false);
  const [selfTest, setSelfTest] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [uiIncidents, setUiIncidents] = useState(() => getRuntimeIncidents());
  const [toast, setToast] = useState(null);

  const [exerciseSet, setExerciseSet] = useState(() =>
    agent.generate({
      experiment: model,
      params: runner.params,
      difficulty: 'standard',
      exerciseCount: 4,
      thinkingCount: 2,
      seed: 20261021,
    }),
  );

  const stale =
    !exerciseSet ||
    paramsFingerprint(exerciseSet.params) !== paramsFingerprint(params) ||
    exerciseSet.difficulty !== difficulty ||
    exerciseSet.exercises.length !== counts.exercises ||
    exerciseSet.thinking.length !== counts.thinking;

  const pushToast = useCallback((title, message = '', tone = 'ok') => {
    setToast({ id: Date.now(), title, message, tone });
  }, []);

  const clearSamples = useCallback(() => {
    sampleRef.current = [];
    setSamples([]);
  }, []);

  useEffect(() => {
    runner.setSpeed(speed);
  }, [runner, speed]);

  useEffect(() => {
    const unsubscribe = subscribeRuntimeIncidents(setUiIncidents);
    return unsubscribe;
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const result = agent.runSelfTest({ runner });
      setSelfTest(result);
      setSnapshot(runner.getSnapshot());
    }, 260);
    return () => window.clearTimeout(timer);
  }, [agent, runner]);

  useEffect(() => () => {
    if (generationTimer.current) window.clearTimeout(generationTimer.current);
  }, []);

  useEffect(() => {
    const onFullscreen = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => document.removeEventListener('fullscreenchange', onFullscreen);
  }, []);

  useEffect(() => {
    if (snapshot.time === 0) {
      if (sampleRef.current.length) clearSamples();
      return;
    }
    const last = sampleRef.current[sampleRef.current.length - 1];
    if (last && snapshot.time - last.t < SAMPLE_INTERVAL) return;
    const sample = {
      t: snapshot.time,
      energy: snapshot.metrics.energy.total,
      kinetic: snapshot.metrics.energy.kinetic,
      potential: snapshot.metrics.energy.potential,
      momentum: snapshot.metrics.momentum?.total ?? null,
    };
    const next = [...sampleRef.current, sample].slice(-MAX_SAMPLES);
    sampleRef.current = next;
    setSamples(next);
  }, [clearSamples, snapshot]);

  const syncSnapshot = useCallback(() => {
    setSnapshot(runner.getSnapshot());
  }, [runner]);

  const handleSnapshot = useCallback((next) => {
    setSnapshot(next);
  }, []);

  const handleFinish = useCallback(() => {
    setRunning(false);
    setSnapshot(runner.getSnapshot());
    pushToast('实验数据采集完成', '可导出指导书，或重置后对比不同参数。', 'ok');
  }, [pushToast, runner]);

  const handleToggle = useCallback(() => {
    if (runner.finished) {
      runner.reset({ silent: true });
      clearSamples();
      setResetToken((value) => value + 1);
    }
    runner.toggle();
    setRunning(runner.running);
    syncSnapshot();
  }, [clearSamples, runner, syncSnapshot]);

  const handleReset = useCallback(() => {
    runner.reset({ silent: true });
    setRunning(false);
    clearSamples();
    setResetToken((value) => value + 1);
    syncSnapshot();
    pushToast('实验已重置', '初始条件与守恒基线已重新载入。', 'ok');
  }, [clearSamples, pushToast, runner, syncSnapshot]);

  const handleStep = useCallback(() => {
    if (runner.finished) {
      runner.reset({ silent: true });
      clearSamples();
    }
    runner.pause();
    runner.stepFrame(1 / 30);
    setRunning(false);
    syncSnapshot();
  }, [clearSamples, runner, syncSnapshot]);

  const handleParamChange = useCallback(
    (key, value) => {
      const field = model.schema.find((item) => item.key === key);
      const reset = field?.apply === 'reset';
      runner.setParams({ [key]: value }, { reset, silent: true });
      const nextSnapshot = runner.getSnapshot();
      setParams({ ...runner.params });
      setSnapshot(nextSnapshot);
      setRunning(runner.running);
      clearSamples();
      if (reset) {
        setResetToken((token) => token + 1);
      }
    },
    [clearSamples, model.schema, runner],
  );

  const handleResetDefaults = useCallback(() => {
    runner.setParams(defaultsFor(model), { reset: true, silent: true });
    setParams({ ...runner.params });
    setRunning(false);
    clearSamples();
    setResetToken((token) => token + 1);
    syncSnapshot();
    pushToast('已恢复默认参数', '实验回到该模型的推荐初始条件。', 'ok');
  }, [clearSamples, model, pushToast, runner, syncSnapshot]);

  const generate = useCallback(() => {
    if (generationTimer.current) window.clearTimeout(generationTimer.current);
    setGenerating(true);
    generationTimer.current = window.setTimeout(() => {
      try {
        const next = agent.generate({
          experiment: model,
          params: runner.params,
          difficulty,
          exerciseCount: counts.exercises,
          thinkingCount: counts.thinking,
          seed: Date.now() % 2147483647,
        });
        setExerciseSet(next);
        pushToast(
          '练习与解析已生成',
          `${next.counts.exercises} 道练习 · ${next.counts.thinking} 道思考题，答案与当前参数一致。`,
          'ok',
        );
      } catch (error) {
        pushToast('生成失败', error instanceof Error ? error.message : String(error), 'error');
      } finally {
        setGenerating(false);
      }
    }, 460);
  }, [agent, counts, difficulty, model, pushToast, runner]);

  const ensureExerciseSet = useCallback(() => {
    if (!stale && exerciseSet) return exerciseSet;
    const next = agent.generate({
      experiment: model,
      params: runner.params,
      difficulty,
      exerciseCount: counts.exercises,
      thinkingCount: counts.thinking,
      seed: Date.now() % 2147483647,
    });
    setExerciseSet(next);
    return next;
  }, [agent, counts, difficulty, exerciseSet, model, runner, stale]);

  const exportGuide = useCallback(
    (includeAnswers) => {
      try {
        const set = ensureExerciseSet();
        const latest = runner.getSnapshot();
        const guide = agent.buildGuide({
          experiment: model,
          params: latest.params,
          metrics: latest.metrics,
          exerciseSet: set,
          includeAnswers,
          generatedAt: new Date(),
        });
        downloadTextFile(guide.filename, guide.markdown);
        pushToast(
          includeAnswers ? '教师版指导书已导出' : '学生版指导书已导出',
          guide.filename,
          'ok',
        );
      } catch (error) {
        pushToast('导出失败', error instanceof Error ? error.message : String(error), 'error');
      }
    },
    [agent, ensureExerciseSet, model, pushToast, runner],
  );

  const runSelfTest = useCallback(() => {
    const result = agent.runSelfTest({ runner });
    setSelfTest(result);
    setSnapshot(runner.getSnapshot());
    if (result.summary.ok) {
      pushToast('内置自检全部通过', `${result.summary.passed}/${result.summary.total} 项守恒与护栏检查正常。`, 'ok');
    } else {
      pushToast('自检发现异常', result.repaired ? '已自动触发步长修复，请查看诊断日志。' : '请查看诊断日志。', 'error');
    }
  }, [agent, pushToast, runner]);

  const handleClearLogs = useCallback(() => {
    runner.diagnostics.clear();
    clearRuntimeIncidents();
    setUiIncidents([]);
    syncSnapshot();
  }, [runner, syncSnapshot]);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await stageRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (error) {
      pushToast('无法进入全屏', error instanceof Error ? error.message : '浏览器拒绝了全屏请求', 'warn');
    }
  }, [pushToast]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = event.target?.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || event.target?.isContentEditable) return;
      if (drawerOpen) return;
      if (event.code === 'Space') {
        event.preventDefault();
        handleToggle();
      } else if (event.key === 'r' || event.key === 'R') {
        handleReset();
      } else if (event.key === 'p' || event.key === 'P') {
        onTogglePresentation();
      } else if (event.key === 'f' || event.key === 'F') {
        toggleFullscreen();
      } else if (/^[1-4]$/.test(event.key)) {
        onSelectLab(experimentIds[Number(event.key) - 1]);
      } else if (event.key === 'Escape' && presentation) {
        onTogglePresentation();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    drawerOpen,
    handleReset,
    handleToggle,
    onSelectLab,
    onTogglePresentation,
    presentation,
    toggleFullscreen,
  ]);

  const experimentIndex = experimentIds.indexOf(labId) + 1;

  return (
    <>
      <TopBar
        model={model}
        onSelectLab={onSelectLab}
        presentation={presentation}
        onTogglePresentation={onTogglePresentation}
        selfTest={selfTest}
        repairs={snapshot.repairs}
        onOpenDiagnostics={() => setDrawerOpen(true)}
        running={running}
      />

      <main className="workspace">
        <section className="stage-column">
          <div className="stage-panel" ref={stageRef}>
            <div className="canvas-shell">
              <SceneCanvas
                runner={runner}
                model={model}
                renderer={renderer}
                options={options}
                resetToken={resetToken}
                onSnapshot={handleSnapshot}
                onFinish={handleFinish}
              />
              <div className="stage-head">
                <div className="stage-title">
                  <div className="eyebrow">
                    <span className="exp-index">EXP {String(experimentIndex).padStart(2, '0')}</span>
                    <span className="eyebrow-divider" />
                    {model.meta.grade}
                  </div>
                  <h1>{model.meta.title}</h1>
                  <p>{model.meta.tagline}</p>
                </div>
                <div className="stage-tools">
                  <span className={`live-chip ${running ? 'active' : ''}`}>
                    <i />
                    {running ? 'LIVE' : 'PAUSED'}
                  </span>
                  <button type="button" className="stage-export" onClick={() => exportGuide(true)}>
                    <Icon name="download" size={15} />
                    导出指导书
                  </button>
                </div>
              </div>

              <div className="stage-legend">
                {MODEL_LEGENDS[model.id].map((item) => (
                  <span key={item.label}>
                    <i className={`legend-dot ${item.className}`} /> {item.label}
                  </span>
                ))}
                <span className="keyboard-hint">空格 播放 · R 重置 · P 课堂模式</span>
              </div>

              {snapshot.finished && (
                <div className="stage-finished">
                  <Icon name="check" size={15} />
                  数据采集完成 · 可重置后继续
                </div>
              )}
            </div>

            <TransportBar
              running={running}
              finished={snapshot.finished}
              speed={speed}
              time={snapshot.time}
              onToggle={handleToggle}
              onReset={handleReset}
              onStep={handleStep}
              onSpeedChange={setSpeed}
              vectors={options.vectors}
              grid={options.grid}
              trail={options.trail}
              onToggleVectors={() => setOptions((value) => ({ ...value, vectors: !value.vectors }))}
              onToggleGrid={() => setOptions((value) => ({ ...value, grid: !value.grid }))}
              onToggleTrail={() => setOptions((value) => ({ ...value, trail: !value.trail }))}
              onFullscreen={toggleFullscreen}
              fullscreen={fullscreen}
            />
          </div>

          <div className="insight-grid">
            <TelemetryChart samples={samples} snapshot={snapshot} running={running} />
            <ConservationCard
              snapshot={snapshot}
              selfTest={selfTest}
              onOpenDiagnostics={() => setDrawerOpen(true)}
            />
          </div>
        </section>

        <aside className="right-panel">
          <div className="panel-tabs" role="tablist" aria-label="侧边面板">
            <button
              type="button"
              role="tab"
              aria-selected={panel === 'params'}
              className={panel === 'params' ? 'active' : ''}
              onClick={() => onPanel('params')}
            >
              <Icon name="sliders" size={16} />
              参数
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={panel === 'data'}
              className={panel === 'data' ? 'active' : ''}
              onClick={() => onPanel('data')}
            >
              <Icon name="chart" size={16} />
              数据
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={panel === 'agent'}
              className={panel === 'agent' ? 'active' : ''}
              onClick={() => onPanel('agent')}
            >
              <Icon name="sparkles" size={16} />
              Agent
              {stale && <span className="tab-alert-dot" />}
            </button>
          </div>

          <div className="panel-body">
            {panel === 'params' && (
              <ParameterPanel
                model={model}
                params={params}
                onChange={handleParamChange}
                onResetDefaults={handleResetDefaults}
              />
            )}
            {panel === 'data' && (
              <DataPanel
                model={model}
                snapshot={snapshot}
                selfTest={selfTest}
                onRunSelfTest={runSelfTest}
                onOpenDiagnostics={() => setDrawerOpen(true)}
              />
            )}
            {panel === 'agent' && (
              <AgentPanel
                model={model}
                snapshot={snapshot}
                exerciseSet={exerciseSet}
                generating={generating}
                stale={stale}
                difficulty={difficulty}
                counts={counts}
                onDifficulty={setDifficulty}
                onCounts={setCounts}
                onGenerate={generate}
                onExport={() => exportGuide(true)}
                onExportStudent={() => exportGuide(false)}
                selfTest={selfTest}
                onOpenDiagnostics={() => setDrawerOpen(true)}
              />
            )}
          </div>
        </aside>
      </main>

      <DiagnosticsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        snapshot={snapshot}
        selfTest={selfTest}
        onRunSelfTest={runSelfTest}
        uiIncidents={uiIncidents}
        onClearLogs={handleClearLogs}
      />
      <Toast toast={toast} onDismiss={() => setToast(null)} />
      {presentation && (
        <button type="button" className="presentation-exit" onClick={onTogglePresentation}>
          <Icon name="minimize" size={16} />
          退出课堂模式
        </button>
      )}
    </>
  );
}
